import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PatientSessionCacheService implements OnDestroy {
  private readonly sessionsKey = 'CVA_PATIENT_SESSIONS';
  private readonly activityKey = 'CVA_PATIENT_SESSION_ACTIVITY';
  private timer?: ReturnType<typeof setInterval>;
  private readonly onResume = () => this.purgeExpired();
  private readonly expiredPatients = new Subject<readonly string[]>();
  readonly expiredPatientKeys$ = this.expiredPatients.asObservable();
  private readonly onStorage = (event: StorageEvent) => {
    if ([this.sessionsKey, this.activityKey, 'CVA_CLIPBOARD_PROFILE', 'consultationForm'].includes(event.key || '')) this.purgeExpired();
  };

  constructor(private readonly zone: NgZone) {}

  start(): void {
    if (this.timer) return;
    this.purgeExpired();
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(this.onResume, 30_000);
      window.addEventListener('focus', this.onResume);
      window.addEventListener('storage', this.onStorage);
      document.addEventListener('visibilitychange', this.onResume);
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    window.removeEventListener('focus', this.onResume);
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onResume);
  }

  private read<T>(key: string): Record<string, T> {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch {
      return {};
    }
  }

  private get timeoutMs(): number {
    const minutes = environment.patientSessionInactivityMinutes;
    return (Number.isFinite(minutes) && minutes > 0 ? minutes : 1440) * 60_000;
  }

  private writeActivity(activity: Record<string, number>): void {
    // Retention metadata must never interrupt recording, note generation or form edits.
    try {
      localStorage.setItem(this.activityKey, JSON.stringify(activity));
    } catch {
      // Storage may be unavailable or full; existing consultation actions can continue.
    }
  }

  recordActivity(patientKey: string | null): void {
    if (!patientKey?.trim()) return;
    this.purgeExpired();
    const activity = this.read<number>(this.activityKey);
    activity[patientKey.trim().toLowerCase()] = Date.now();
    this.writeActivity(activity);
  }

  canPersist(patientKey: string): boolean {
    const lastActivity = this.read<number>(this.activityKey)[patientKey.trim().toLowerCase()];
    return typeof lastActivity !== 'number' || Date.now() - lastActivity < this.timeoutMs;
  }

  canRestoreClipboardPatient(patientKey: string): boolean {
    this.purgeExpired();
    if (!patientKey.trim()) return false;
    const key = patientKey.trim().toLowerCase();
    const activity = this.read<number>(this.activityKey);
    if (!Number.isFinite(activity[key])) {
      activity[key] = Date.now();
      this.writeActivity(activity);
    }
    return true;
  }

  readSessions<T>(): Record<string, T> {
    this.purgeExpired();
    return this.read<T>(this.sessionsKey);
  }

  saveSession(patientKey: string, session: unknown): void {
    const sessions = this.readSessions<unknown>();
    if (!this.canPersist(patientKey)) return;
    const key = patientKey.trim().toLowerCase();
    const activity = this.read<number>(this.activityKey);
    if (typeof activity[key] !== 'number') {
      activity[key] = Date.now();
      this.writeActivity(activity);
    }
    sessions[key] = session;
    localStorage.setItem(this.sessionsKey, JSON.stringify(sessions));
  }

  purgeExpired(): void {
    try {
      this.purgeExpiredEntries();
    } catch {
      // Cleanup is best-effort; storage errors must not prevent application startup or restore.
    }
  }

  private purgeExpiredEntries(): void {
    const sessions = this.read<unknown>(this.sessionsKey);
    const activity = this.read<number>(this.activityKey);
    const now = Date.now();
    const draft = this.read<unknown>('consultationForm');
    const name = typeof draft['patientName'] === 'string' ? draft['patientName'].trim().toLowerCase() : '';
    const profile = this.read<unknown>('CVA_CLIPBOARD_PROFILE');
    const profileName = `${profile['first_name'] || ''} ${profile['last_name'] || ''}`.trim().toLowerCase();
    let activityChanged = false;
    let sessionsChanged = false;
    for (const key of new Set([...Object.keys(sessions), name, profileName].filter(Boolean))) {
      if (!Number.isFinite(activity[key])) {
        // Legacy entries have no reliable activity date. Give them one full retention period.
        activity[key] = now;
        activityChanged = true;
      }
      if (key in sessions && now - activity[key] >= this.timeoutMs) {
        delete sessions[key];
        sessionsChanged = true;
      }
    }
    if (activityChanged) this.writeActivity(activity);
    if (sessionsChanged) localStorage.setItem(this.sessionsKey, JSON.stringify(sessions));

    // Keep expired timestamps so passive saves cannot resurrect a purged patient's data.
    if (name && Number.isFinite(activity[name]) && now - activity[name] >= this.timeoutMs) {
      for (const key of ['consultationForm', 'tabs', 'savedTranscriptionId', 'editingTranscriptionId']) {
        localStorage.removeItem(key);
      }
    }
    if (profileName && Number.isFinite(activity[profileName]) && now - activity[profileName] >= this.timeoutMs) {
      const retained = {
        first_name: profile['first_name'] || '',
        last_name: profile['last_name'] || '',
        patient_history: profile['patient_history'] || ''
      };
      const serialized = JSON.stringify(retained);
      if (localStorage.getItem('CVA_CLIPBOARD_PROFILE') !== serialized) {
        localStorage.setItem('CVA_CLIPBOARD_PROFILE', serialized);
      }
    }
    // Use timestamps as well as removed entries: another browser tab may have purged first.
    const expiredKeys = Object.keys(activity).filter(key =>
      Number.isFinite(activity[key]) && now - activity[key] >= this.timeoutMs
    );
    if (expiredKeys.length) {
      this.zone.run(() => this.expiredPatients.next(expiredKeys));
    }
  }
}
